import React, { useId } from 'react';

// V1.1 Card Ranks (8 tiers, permanent based on lifetime RP)
export const CARD_RANKS = [
  { id: 1, name: 'Nine',        label: '9',  lifetimeRpRequired: 0 },
  { id: 2, name: 'Ten',         label: '10', lifetimeRpRequired: 200 },
  { id: 3, name: 'Jack',        label: 'J',  lifetimeRpRequired: 600 },
  { id: 4, name: 'Queen',       label: 'Q',  lifetimeRpRequired: 1500 },
  { id: 5, name: 'King',        label: 'K',  lifetimeRpRequired: 4000 },
  { id: 6, name: 'Ace',         label: 'A',  lifetimeRpRequired: 10000 },
  { id: 7, name: 'Left Bower',  label: 'LB', lifetimeRpRequired: 25000 },
  { id: 8, name: 'Right Bower', label: 'RB', lifetimeRpRequired: 60000 },
];

export const SUIT_LEVELS = [
  { level: 1, name: 'Clubs',    symbol: '♣' },
  { level: 2, name: 'Hearts',   symbol: '♥' },
  { level: 3, name: 'Spades',   symbol: '♠' },
  { level: 4, name: 'Diamonds', symbol: '♦' },
];

// Backward-compatible RANK_TIERS
export const RANK_TIERS = CARD_RANKS.map(r => ({ min: r.lifetimeRpRequired, name: r.name, label: r.label }));

export function getRankFromPoints(points) {
  for (let i = RANK_TIERS.length - 1; i >= 0; i--) {
    if (points >= RANK_TIERS[i].min) return RANK_TIERS[i];
  }
  return RANK_TIERS[0];
}

export function getRankProgress(points) {
  const rank = getRankFromPoints(points);
  const tierIndex = RANK_TIERS.findIndex(t => t.name === rank.name);
  const nextTier = RANK_TIERS[tierIndex + 1];
  if (!nextTier) return { rank, nextRank: null, pointsToNext: 0, progress: 1 };
  const pointsToNext = nextTier.min - points;
  const tierRange = nextTier.min - rank.min;
  const progress = (points - rank.min) / tierRange;
  return { rank, nextRank: nextTier, pointsToNext, progress };
}

const isRedSuit = (suit) => suit === '♥' || suit === '♦';

// Crown tier: 0=none, 1=simple, 2=ornate, 3=grand
function getCrownTier(cardRankId) {
  if (cardRankId <= 2) return 0;  // Nine, Ten: no crown
  if (cardRankId <= 4) return 1;  // Jack, Queen: simple
  if (cardRankId <= 6) return 2;  // King, Ace: ornate
  return 3;                        // Left/Right Bower: grand
}

// Border material colors by tier
function getBorderColors(cardRankId) {
  if (cardRankId <= 2) return { stroke: '#8B6914', fill: 'url(#bronze-grad)', glow: false };
  if (cardRankId <= 4) return { stroke: '#9CA3AF', fill: 'url(#silver-grad)', glow: false };
  if (cardRankId <= 6) return { stroke: '#D4AF37', fill: 'url(#gold-grad)', glow: false };
  return { stroke: '#FFD700', fill: 'url(#gold-bright-grad)', glow: true };
}

function isBower(cardRankId) {
  return cardRankId >= 7;
}

// SVG Crown for different tiers — uses unique IDs to avoid gradient collisions
function CrownSVG({ tier, width, uid }) {
  if (tier === 0) return null;
  const h = width * 0.45;
  const w = width * 0.85;

  if (tier === 1) {
    // Simple silver crown — 3 points
    const gid = `cs-${uid}`;
    return (
      <svg width={w} height={h} viewBox="0 0 60 24" style={{ display: 'block', margin: '0 auto' }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#D8D8D8" />
            <stop offset="50%" stopColor="#B0B0B0" />
            <stop offset="100%" stopColor="#888888" />
          </linearGradient>
        </defs>
        <path d="M5,22 L10,8 L20,16 L30,4 L40,16 L50,8 L55,22 Z"
          fill={`url(#${gid})`} stroke="#6B6B6B" strokeWidth="1.5" />
        <rect x="5" y="20" width="50" height="3" rx="1" fill="#B0B0B0" stroke="#6B6B6B" strokeWidth="0.5" />
      </svg>
    );
  }

  if (tier === 2) {
    // Gold crown with jewel accents
    const gid = `cg-${uid}`;
    return (
      <svg width={w} height={h} viewBox="0 0 60 24" style={{ display: 'block', margin: '0 auto' }}>
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#FFD700" />
            <stop offset="50%" stopColor="#D4AF37" />
            <stop offset="100%" stopColor="#B8860B" />
          </linearGradient>
        </defs>
        <path d="M3,22 L8,6 L18,14 L30,2 L42,14 L52,6 L57,22 Z"
          fill={`url(#${gid})`} stroke="#8B6914" strokeWidth="1.5" />
        <rect x="3" y="20" width="54" height="3.5" rx="1" fill="#D4AF37" stroke="#8B6914" strokeWidth="0.5" />
        {/* Jewel accents on points */}
        <circle cx="30" cy="5" r="2" fill="#E53E3E" stroke="#8B0000" strokeWidth="0.5" />
        <circle cx="8" cy="9" r="1.5" fill="#3B82F6" stroke="#1D4ED8" strokeWidth="0.5" />
        <circle cx="52" cy="9" r="1.5" fill="#3B82F6" stroke="#1D4ED8" strokeWidth="0.5" />
      </svg>
    );
  }

  // tier === 3: Grand gold crown with jewels, base band, glow
  const gid = `cgr-${uid}`;
  const jrId = `jr-${uid}`;
  const jbId = `jb-${uid}`;
  const jgId = `jg-${uid}`;
  return (
    <svg width={w} height={h + 4} viewBox="0 0 60 28" style={{ display: 'block', margin: '0 auto', filter: 'drop-shadow(0 0 4px rgba(255,215,0,0.6))' }}>
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#FFF8DC" />
          <stop offset="30%" stopColor="#FFD700" />
          <stop offset="70%" stopColor="#D4AF37" />
          <stop offset="100%" stopColor="#B8860B" />
        </linearGradient>
        <radialGradient id={jrId} cx="50%" cy="50%">
          <stop offset="0%" stopColor="#FF6B6B" />
          <stop offset="100%" stopColor="#C62828" />
        </radialGradient>
        <radialGradient id={jbId} cx="50%" cy="50%">
          <stop offset="0%" stopColor="#60A5FA" />
          <stop offset="100%" stopColor="#1D4ED8" />
        </radialGradient>
        <radialGradient id={jgId} cx="50%" cy="50%">
          <stop offset="0%" stopColor="#6EE7B7" />
          <stop offset="100%" stopColor="#059669" />
        </radialGradient>
      </defs>
      <path d="M2,24 L6,4 L16,12 L23,2 L30,8 L37,2 L44,12 L54,4 L58,24 Z"
        fill={`url(#${gid})`} stroke="#8B6914" strokeWidth="1.2" />
      {/* Base band with gems */}
      <rect x="2" y="22" width="56" height="5" rx="1.5" fill="#D4AF37" stroke="#8B6914" strokeWidth="0.8" />
      <circle cx="15" cy="24.5" r="1.5" fill={`url(#${jgId})`} stroke="#065F46" strokeWidth="0.4" />
      <circle cx="30" cy="24.5" r="1.8" fill={`url(#${jrId})`} stroke="#7F1D1D" strokeWidth="0.4" />
      <circle cx="45" cy="24.5" r="1.5" fill={`url(#${jgId})`} stroke="#065F46" strokeWidth="0.4" />
      {/* Top jewels */}
      <circle cx="23" cy="5" r="2.2" fill={`url(#${jrId})`} stroke="#7F1D1D" strokeWidth="0.5" />
      <circle cx="37" cy="5" r="2.2" fill={`url(#${jrId})`} stroke="#7F1D1D" strokeWidth="0.5" />
      <circle cx="30" cy="10" r="1.5" fill={`url(#${jbId})`} stroke="#1E3A5F" strokeWidth="0.5" />
      <circle cx="6" cy="7" r="1.5" fill={`url(#${jbId})`} stroke="#1E3A5F" strokeWidth="0.5" />
      <circle cx="54" cy="7" r="1.5" fill={`url(#${jbId})`} stroke="#1E3A5F" strokeWidth="0.5" />
    </svg>
  );
}

// Laurel wreath for Bower ranks
function WreathSVG({ width, height, uid }) {
  const w = width * 1.5;
  const h = height * 1.2;
  const lgId = `lg-${uid}`;
  return (
    <svg width={w} height={h} viewBox="0 0 120 100" style={{
      position: 'absolute',
      top: '50%', left: '50%',
      transform: 'translate(-50%, -48%)',
      zIndex: 0,
      pointerEvents: 'none',
    }}>
      <defs>
        <linearGradient id={lgId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2D8B4E" />
          <stop offset="100%" stopColor="#1A5C34" />
        </linearGradient>
      </defs>
      {/* Left branch */}
      <g transform="translate(10, 10)">
        {/* Main stem */}
        <path d="M45,85 Q20,60 15,35 Q12,20 20,8" fill="none" stroke="#2D6B3F" strokeWidth="2" />
        {/* Leaves */}
        {[
          { x: 18, y: 15, r: -30 }, { x: 16, y: 25, r: -20 },
          { x: 15, y: 35, r: -10 }, { x: 18, y: 45, r: 0 },
          { x: 22, y: 55, r: 10 }, { x: 28, y: 65, r: 20 },
          { x: 35, y: 75, r: 30 },
        ].map((leaf, i) => (
          <ellipse key={`ll${i}`} cx={leaf.x} cy={leaf.y} rx="8" ry="4"
            transform={`rotate(${leaf.r}, ${leaf.x}, ${leaf.y})`}
            fill={`url(#${lgId})`} opacity="0.9" />
        ))}
        {/* Berries */}
        <circle cx="22" cy="20" r="2" fill="#C62828" />
        <circle cx="19" cy="40" r="2" fill="#C62828" />
        <circle cx="30" cy="70" r="2" fill="#C62828" />
      </g>
      {/* Right branch (mirrored) */}
      <g transform="translate(110, 10) scale(-1, 1)">
        <path d="M45,85 Q20,60 15,35 Q12,20 20,8" fill="none" stroke="#2D6B3F" strokeWidth="2" />
        {[
          { x: 18, y: 15, r: -30 }, { x: 16, y: 25, r: -20 },
          { x: 15, y: 35, r: -10 }, { x: 18, y: 45, r: 0 },
          { x: 22, y: 55, r: 10 }, { x: 28, y: 65, r: 20 },
          { x: 35, y: 75, r: 30 },
        ].map((leaf, i) => (
          <ellipse key={`rl${i}`} cx={leaf.x} cy={leaf.y} rx="8" ry="4"
            transform={`rotate(${leaf.r}, ${leaf.x}, ${leaf.y})`}
            fill={`url(#${lgId})`} opacity="0.9" />
        ))}
        <circle cx="22" cy="20" r="2" fill="#C62828" />
        <circle cx="19" cy="40" r="2" fill="#C62828" />
        <circle cx="30" cy="70" r="2" fill="#C62828" />
      </g>
      {/* Gold ribbon tie at bottom */}
      <path d="M52,90 Q60,95 68,90" fill="none" stroke="#D4AF37" strokeWidth="3" strokeLinecap="round" />
      <path d="M54,90 L50,98 L56,94" fill="#D4AF37" />
      <path d="M66,90 L70,98 L64,94" fill="#D4AF37" />
    </svg>
  );
}

// Sizes for the badge
const SIZES = {
  sm: { w: 42, h: 58, labelFont: 16, suitFont: 14, cornerFont: 9, ptFont: 8, bowerFont: 8, borderWidth: 2 },
  md: { w: 56, h: 78, labelFont: 22, suitFont: 18, cornerFont: 11, ptFont: 9, bowerFont: 10, borderWidth: 2.5 },
  lg: { w: 76, h: 106, labelFont: 30, suitFont: 24, cornerFont: 14, ptFont: 11, bowerFont: 13, borderWidth: 3 },
};

/**
 * CrownFrameBadge — V1.1 Crown Frame rank badge
 * Props:
 *   cardRankId: 1-8 (or derive from points/rankName)
 *   suitLevel: 1-4 (or derive from suitSymbol)
 *   suitSymbol: '♣', '♥', '♠', '♦'
 *   points: active RP (for backward compat)
 *   preferredSuit: fallback suit symbol
 *   size: 'sm' | 'md' | 'lg'
 *   showPoints: show points below badge
 */
export default function CrownFrameBadge({
  cardRankId, suitLevel, suitSymbol,
  points, preferredSuit = '♠', rankName, rankLabel,
  size = 'md', showPoints = true, badgeLabel,
}) {
  const uid = useId();

  // Resolve card rank
  let resolvedRankId = cardRankId;
  if (!resolvedRankId && rankName) {
    const found = CARD_RANKS.find(r => r.name === rankName);
    resolvedRankId = found ? found.id : 1;
  }
  if (!resolvedRankId && points !== undefined) {
    for (let i = CARD_RANKS.length - 1; i >= 0; i--) {
      if (points >= CARD_RANKS[i].lifetimeRpRequired) { resolvedRankId = CARD_RANKS[i].id; break; }
    }
  }
  resolvedRankId = resolvedRankId || 1;

  const cardRank = CARD_RANKS.find(r => r.id === resolvedRankId) || CARD_RANKS[0];

  // Resolve suit
  let displaySuit = suitSymbol || preferredSuit || '♣';
  if (!suitSymbol && suitLevel) {
    const sl = SUIT_LEVELS.find(s => s.level === suitLevel);
    if (sl) displaySuit = sl.symbol;
  }

  const s = SIZES[size] || SIZES.md;
  const red = isRedSuit(displaySuit);
  const suitColor = red ? '#C62828' : '#1A1A1A';
  const crownTier = getCrownTier(resolvedRankId);
  const border = getBorderColors(resolvedRankId);
  const showWreath = isBower(resolvedRankId);
  const isBowerRank = isBower(resolvedRankId);
  const label = cardRank.label;

  // Render center content
  const renderCenterContent = () => {
    if (label === 'LB' || label === 'RB') {
      const word1 = label === 'LB' ? 'Left' : 'Right';
      return (
        <div style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', gap: 1, zIndex: 1, position: 'relative',
        }}>
          <span style={{
            fontSize: s.bowerFont, fontFamily: "'Georgia', serif",
            fontStyle: 'italic', fontWeight: 700, color: suitColor,
            letterSpacing: 1, lineHeight: 1.1,
          }}>{word1}</span>
          <span style={{
            width: s.w * 0.4, height: 1,
            background: `linear-gradient(90deg, transparent, ${suitColor}, transparent)`,
          }} />
          <span style={{
            fontSize: s.bowerFont, fontFamily: "'Georgia', serif",
            fontStyle: 'italic', fontWeight: 700, color: suitColor,
            letterSpacing: 1, lineHeight: 1.1,
          }}>Bower</span>
          <span style={{ fontSize: s.suitFont * 0.7, color: suitColor, lineHeight: 1, marginTop: 2 }}>
            {displaySuit}
          </span>
        </div>
      );
    }
    return (
      <span style={{
        fontSize: s.labelFont, fontFamily: "'Georgia', serif",
        fontWeight: 700, color: suitColor, lineHeight: 1, zIndex: 1, position: 'relative',
      }}>{label}</span>
    );
  };

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: badgeLabel ? 6 : 0 }}>
      {badgeLabel && (
        <span style={{
          fontSize: s.ptFont + 1,
          color: '#8b949e',
          fontWeight: 600,
          fontFamily: "'Georgia', serif",
          whiteSpace: 'nowrap',
          letterSpacing: 0.5,
        }}>{badgeLabel}</span>
      )}
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>
      {/* Crown above card */}
      {crownTier > 0 && <CrownSVG tier={crownTier} width={s.w} uid={uid} />}

      {/* Wreath + Card wrapper */}
      <span style={{
        position: 'relative', display: 'inline-flex', alignItems: 'center',
        justifyContent: 'center', marginTop: crownTier > 0 ? -2 : 0,
      }}>
        {/* Laurel wreath for Bower ranks */}
        {showWreath && <WreathSVG width={s.w} height={s.h} uid={uid} />}

        {/* Card frame */}
        <span style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: s.w,
          height: s.h,
          background: '#FFFFFF',
          border: `${s.borderWidth}px solid ${border.stroke}`,
          borderRadius: 6,
          overflow: 'hidden',
          boxShadow: border.glow
            ? `0 2px 8px rgba(0,0,0,0.25), 0 0 12px rgba(255,215,0,0.4)`
            : '0 2px 6px rgba(0,0,0,0.25)',
          zIndex: 1,
        }}>
          {/* Top-left corner: rank + suit */}
          {!isBowerRank && (
            <span style={{
              position: 'absolute', top: 2, left: 3,
              display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1,
            }}>
              <span style={{ fontSize: s.cornerFont, fontWeight: 700, fontFamily: "'Georgia', serif", color: suitColor }}>{label}</span>
              <span style={{ fontSize: s.cornerFont - 1, color: suitColor }}>{displaySuit}</span>
            </span>
          )}

          {/* Bottom-right corner: rank + suit (rotated) */}
          {!isBowerRank && (
            <span style={{
              position: 'absolute', bottom: 2, right: 3, transform: 'rotate(180deg)',
              display: 'flex', flexDirection: 'column', alignItems: 'center', lineHeight: 1,
            }}>
              <span style={{ fontSize: s.cornerFont, fontWeight: 700, fontFamily: "'Georgia', serif", color: suitColor }}>{label}</span>
              <span style={{ fontSize: s.cornerFont - 1, color: suitColor }}>{displaySuit}</span>
            </span>
          )}

          {/* Center content */}
          {renderCenterContent()}

          {/* Suit symbol below center for non-bower */}
          {!isBowerRank && (
            <span style={{
              position: 'absolute', bottom: s.h * 0.22,
              fontSize: s.suitFont * 0.7, color: suitColor, opacity: 0.3,
            }}>{displaySuit}</span>
          )}
        </span>
      </span>

      {/* Points below card */}
      {showPoints && points !== undefined && points !== null && (
        <span style={{
          fontSize: s.ptFont,
          color: '#8b949e',
          fontWeight: 600,
          marginTop: 2,
          whiteSpace: 'nowrap',
        }}>{points} RP</span>
      )}
    </span>
    </span>
  );
}
